/**
 * Pure filter + presentation helpers for the Feedback & Ideas queue (Trello #371).
 *
 * Deliberately separate from FeedbackPage.tsx: the page's own test file is a
 * router-heavy MUI view that can't be executed locally on Windows (react-router
 * trips `TextEncoder is not a constructor` under jsdom — see
 * reference_legal_ai_admin_vitest_textencoder), so anything with real logic
 * lives here where it CAN be run and verified before pushing.
 */

import {
  PLATFORM_FEEDBACK_STATUS_LABELS,
  PLATFORM_FEEDBACK_TYPE_LABELS,
  PlatformFeedbackRow,
  PlatformFeedbackStatus,
  PlatformFeedbackType,
} from '../../services/api';

/** `'all'` is the "no filter" sentinel the dropdowns start on. */
export const ALL = 'all' as const;
export type TypeFilter = PlatformFeedbackType | typeof ALL;
export type StatusFilter = PlatformFeedbackStatus | typeof ALL;

export interface PlatformFeedbackFilters {
  type: TypeFilter;
  status: StatusFilter;
  /** Free-text match over title, description and submitter. */
  search: string;
}

export const EMPTY_FILTERS: PlatformFeedbackFilters = {
  type: ALL,
  status: ALL,
  search: '',
};

/**
 * Apply the type + status + search filters.
 *
 * Search is case-insensitive and spans title, description and submitter so that
 * "the one about uploads" and "that thing bob sent" both land — an admin
 * looking for a specific submission rarely remembers which field it was in.
 */
export function filterPlatformFeedback(
  rows: PlatformFeedbackRow[],
  filters: PlatformFeedbackFilters,
): PlatformFeedbackRow[] {
  const needle = filters.search.trim().toLowerCase();

  return rows.filter((row) => {
    if (filters.type !== ALL && row.type !== filters.type) return false;
    if (filters.status !== ALL && row.status !== filters.status) return false;
    if (!needle) return true;

    const haystack = [row.title, row.description, row.email, row.userId, row.organizationId]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}

/**
 * MUI Chip colour per status. `new` is deliberately the loud one — it's the
 * only state that means "nobody has looked at this yet".
 */
export function statusChipColor(
  status: PlatformFeedbackStatus,
): 'default' | 'info' | 'primary' | 'success' | 'warning' {
  switch (status) {
    case 'new':
      return 'warning';
    case 'under_review':
      return 'info';
    case 'planned':
    case 'in_development':
      return 'primary';
    case 'completed':
      return 'success';
    case 'not_proceeding':
      return 'default';
  }
}

/**
 * Label for a stored value, falling back to the raw string so a value written
 * by a newer backend renders as itself rather than vanishing to a blank cell.
 */
export function typeLabel(type: PlatformFeedbackType | string): string {
  return PLATFORM_FEEDBACK_TYPE_LABELS[type as PlatformFeedbackType] ?? type;
}

export function statusLabel(status: PlatformFeedbackStatus | string): string {
  return PLATFORM_FEEDBACK_STATUS_LABELS[status as PlatformFeedbackStatus] ?? status;
}

/** Who sent it — email when we have it, else the raw Cognito id. */
export function submitterLabel(row: PlatformFeedbackRow): string {
  return row.email || row.userId || '-';
}
